import axios from 'axios';
import { storeAction } from '../storeActions.constant';
import { storeGetters } from '../storeGetters.constant';
import { storeMutation } from '../storeMutations.constant';
import { getBasicHeaders } from "../../common/utils/utils";
import { wrapAction } from "../../common/utils/actionLifecycle";
import {
    getBasicLoginSecurityOptions,
    getPasswordComplexityRequirement,
    loginSecurityConstants,
} from "../../components/MyAccount/myaccount.constants";

const manageLoginSecurityState = {
    baseURL: process.env.VUE_APP_BASE_URL,
    userMFASettings: [],
    isUserInAmfaGroup: false,
    loginSecuritySettings: getBasicLoginSecurityOptions(),
    securityQuestionsList: [],    
    passwordComplexityRequirements: [],
    userInformation: null,
    smsValidatedConfirm: false,
};

const manageLoginSecurityMutations = {
    [storeMutation.setUserMFASettings]: (state, data) => {
        state.userMFASettings = data;
    },
    [storeMutation.setUserInAmfaGroupValue]: (state, data) => {
        state.isUserInAmfaGroup = data;
    },
    [storeMutation.setUserInfo]: (state, data) => {
        state.userInformation = data;
    },
    [storeMutation.setSecurityQuestionsList]: (state, data) => {
        state.securityQuestionsList = data;
    },
    [storeMutation.setPhoneValidated]: (state, phoneConfirmation) => {
        state.smsValidatedConfirm = !!phoneConfirmation;
    },
    [storeMutation.setLoginSecuritySettings]: (state, data) => {
            state.loginSecuritySettings = getBasicLoginSecurityOptions();
            state.loginSecuritySettings.forEach((loginSetting) => {
                const settingFromData = data.find(mfaSetting => mfaSetting.factorType === loginSetting.factorType);
                if (settingFromData) {
                    loginSetting.isEnabled = settingFromData.status === loginSecurityConstants.FACTOR_STATUS_ACTIVE || settingFromData.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION;
                    loginSetting.inEditMode = settingFromData.factorType === loginSecurityConstants.FACTOR_SMS ?
                        settingFromData.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION || state.smsValidatedConfirm :
                        settingFromData.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION;
                    loginSetting.isActivationCodeSent = settingFromData.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION;
                    loginSetting.sendCodeErrorMsg = "";
                    loginSetting.confirmAuthenticationErrorMsg = "";
                    loginSetting.metadata = { ...settingFromData };
                }
            });
            state.loginSecuritySettings = state.loginSecuritySettings.slice(0);
    },
    [storeMutation.cancelLoginSecurityEdit]: (state, factorType) => {
        if (factorType) {
            const factorFromState = state.userMFASettings.find(setting => setting.factorType === factorType);
            if (factorFromState) {
             state.loginSecuritySettings = state.loginSecuritySettings.slice(0).map((setting) => {
                 return setting.factorType === factorType
                     ? {
                        ...setting,
                         isEnabled: factorFromState.status === loginSecurityConstants.FACTOR_STATUS_ACTIVE || factorFromState.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION,
                         inEditMode: false,
                         isActivationCodeSent: factorFromState.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION,
                         sendCodeErrorMsg: "",
                         confirmAuthenticationErrorMsg: "",
                         metadata: { ...factorFromState },
                     }
                     : { ...setting };
             });
            }
        }
    },
    [storeMutation.setPasswordComplexityRequirement]: (state, passwordSettings) => {
        if (passwordSettings && passwordSettings.settings
            && passwordSettings.settings.password && passwordSettings.settings.password.complexity) {
            state.passwordComplexityRequirements =
                getPasswordComplexityRequirement(passwordSettings.settings.password.complexity);
        }
    },
};

const manageLoginSecurityActions = {
    [storeAction.getUserMFASettings]: wrapAction(storeAction.getUserMFASettings, ({ state, commit, dispatch }) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/get-multi-factors-by-user`,
            //url: '/mocks/manageLoginSecurity.json',
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                if (response && response.data) {
                    if (response.data.multiFactors) {
                        const { multiFactors } = response.data;
                        const settingWithPendingActivation = multiFactors
                            .some(setting => setting && setting.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION);
                        if (settingWithPendingActivation || state.smsValidatedConfirm) {
                            multiFactors.forEach(async (setting) => {
                                if (setting && setting.status === loginSecurityConstants.FACTOR_STATUS_PENDING_ACTIVATION) {
                                    await dispatch(storeAction.removeMFASettings, setting);
                                }
                                if (state.smsValidatedConfirm && setting && setting.factorType === loginSecurityConstants.FACTOR_SMS) {
                                    await dispatch(storeAction.removeMFASettings, setting);
                                }
                            });
                        } else {
                            commit(storeMutation.setPhoneValidated, false);
                            commit(storeMutation.setUserMFASettings, multiFactors);
                            commit(storeMutation.setLoginSecuritySettings, multiFactors);
                        }
                    }
                    if (response.data.isAmfaGroup) {
                        commit(storeMutation.setUserInAmfaGroupValue, true);
                    }
                    return response.data;
                }
            })
            .catch((err) => {
                throw err;
            });
    }),
    [storeAction.getAllSecurityQuestions]: wrapAction(storeAction.getAllSecurityQuestions, ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/get-all-security-questions`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setSecurityQuestionsList, response.data);
                return response.data;
            })
            .catch((err) => {
                throw err;
            });
    }),
    [storeAction.removeMFASettings]: wrapAction(storeAction.removeMFASettings, ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/delete-multi-factor-auth`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                if (response && response.data) {
                    commit(storeMutation.setPhoneValidated, false);
                    commit(storeMutation.setUserMFASettings, response.data);
                    commit(storeMutation.setLoginSecuritySettings, response.data);
                    return response.data;
                }
            })
            .catch((err) => {
                throw err.response.data.message;
            });
    }),
    [storeAction.saveMFASettings]: wrapAction(storeAction.saveMFASettings, ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/save-multi-factor-auth-by-user`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                if (response && response.data) {
                    const smsSetting = response.data.find(setting => setting.factorType === loginSecurityConstants.FACTOR_SMS);
                    const phoneValidated = requestBody.factorType === loginSecurityConstants.FACTOR_SMS &&
                        smsSetting && smsSetting.status === loginSecurityConstants.FACTOR_STATUS_ACTIVE;
                    commit(storeMutation.setPhoneValidated, phoneValidated);
                    commit(storeMutation.setUserMFASettings, response.data);
                    commit(storeMutation.setLoginSecuritySettings, response.data);
                    return response.data;
                }
            })
            .catch((err) => {
                throw err.response.data.message;
            });
    }),
    [storeAction.resendMFASettings]: wrapAction(storeAction.resendMFASettings, ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/resend-multi-factor-passcode`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                if (response && response.data) {
                    commit(storeMutation.setPhoneValidated, false);
                    commit(storeMutation.setUserMFASettings, response.data);
                    commit(storeMutation.setLoginSecuritySettings, response.data);
                    return response.data;
                }
            })
            .catch((err) => {
                throw err.response.data.message;
            });
    }),
    [storeAction.confirmMFASettings]: wrapAction(storeAction.confirmMFASettings, ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/confirm-multi-factor-passcode`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                commit(storeMutation.setUserMFASettings, response.data);
                commit(storeMutation.setLoginSecuritySettings, response.data);
                return response.data;
            })
            .catch((err) => {
                throw err.response.data.message;
            });
    }),
    [storeAction.getPasswordPolicies]: wrapAction(storeAction.getPasswordPolicies, ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/get-password-policy-settings`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setPasswordComplexityRequirement, response.data);
                return response.data;
            })
            .catch((err) => {
                throw err;
            });
    }),
};

const manageLoginSecurityGetters = {
    [storeGetters.userMFASettings]: state => state.userMFASettings,
    [storeGetters.loginSecuritySettings]: state => state.loginSecuritySettings,
    [storeGetters.isUserInAmfaGroup]: state => state.isUserInAmfaGroup,
    [storeGetters.securityQuestionsList]: state => state.securityQuestionsList,
    [storeGetters.passwordComplexityRequirements]: state => state.passwordComplexityRequirements,
    [storeGetters.userInfo]: state => state.userInformation,
    [storeGetters.getPhoneConfirmation]: state => state.smsValidatedConfirm,
};

export default {
    namespaced: true,
    state: {
        initialState: manageLoginSecurityState,
        ...manageLoginSecurityState,
    },
    mutations: { ...manageLoginSecurityMutations },
    actions: { ...manageLoginSecurityActions },
    getters: { ...manageLoginSecurityGetters },
};
